import {
  NarrativeTemplate,
  TemplateContext,
  generateNarrative,
  formatPercent,
  formatCurrency,
  formatDuration,
  describeTrend,
  describePriority,
  describeTimeUntil
} from './templates'

// Import domain-specific templates
import { fleetTemplates } from './fleet-narratives'
import { assetTemplates } from './asset-narratives'
import { anomalyTemplates } from './anomaly-narratives'
import { maintenanceTemplates } from './maintenance-narratives'
import { executiveTemplates } from './executive-narratives'

// Combine all templates
const allTemplates: NarrativeTemplate[] = [
  ...fleetTemplates,
  ...assetTemplates,
  ...anomalyTemplates,
  ...maintenanceTemplates,
  ...executiveTemplates
]

export class NarrativeGenerator {
  private templates: NarrativeTemplate[]

  constructor(customTemplates?: NarrativeTemplate[]) {
    this.templates = customTemplates || allTemplates
  }

  // Generate fleet-level insight
  generateFleetInsight(data: {
    healthScore: number
    totalAssets: number
    healthyAssets: number
    warningAssets: number
    criticalAssets: number
    anomaliesToday: number
    previousHealthScore?: number
  }): string {
    const context: TemplateContext = {
      ...data,
      healthScoreFormatted: formatPercent(data.healthScore),
      assetsNeedingAttention: data.warningAssets + data.criticalAssets,
      trendDescription: data.previousHealthScore
        ? describeTrend(data.healthScore, data.previousHealthScore)
        : 'stable'
    }

    return generateNarrative(this.templates, context, 'fleet') ||
      `Fleet health is at ${formatPercent(data.healthScore)}. ${data.criticalAssets} assets need immediate attention.`
  }

  // Generate asset-level insight
  generateAssetInsight(data: {
    assetId: string
    healthScore: number
    anomalyScore: number
    anomalyCategory?: string
    topFeatures?: { feature: string; importance: number }[]
    failureTimeHours?: number
    lastAnomaly?: string
  }): string {
    const context: TemplateContext = {
      assetId: data.assetId,
      healthScore: data.healthScore,
      anomalyScore: data.anomalyScore,
      anomalyCategory: data.anomalyCategory,
      failureTimeHours: data.failureTimeHours,
      lastAnomaly: data.lastAnomaly,
      healthScoreFormatted: formatPercent(data.healthScore),
      anomalyScoreFormatted: formatPercent(data.anomalyScore * 100),
      priority: describePriority(data.anomalyScore),
      timeToFailure: data.failureTimeHours
        ? describeTimeUntil(data.failureTimeHours)
        : 'unknown',
      topFeaturesFormatted: data.topFeatures
        ? data.topFeatures.slice(0, 3).map(f =>
            `${f.feature} (${formatPercent(f.importance * 100)} contribution)`
          ).join('\n')
        : 'No significant factors identified'
    }

    return generateNarrative(this.templates, context, 'asset') ||
      `${data.assetId}: Current anomaly score is ${formatPercent(data.anomalyScore * 100)}.`
  }

  // Generate anomaly-specific insight
  generateAnomalyInsight(data: {
    assetId: string
    category: string
    severity: number
    sensors: string[]
    historicalMatches?: number
    recommendation?: string
    confidence: number
  }): string {
    const context: TemplateContext = {
      ...data,
      severityLevel: describePriority(data.severity),
      sensorsFormatted: data.sensors.join(', '),
      confidenceFormatted: formatPercent(data.confidence * 100),
      hasHistoricalMatches: (data.historicalMatches || 0) > 0,
      matchCount: data.historicalMatches || 0
    }

    return generateNarrative(this.templates, context, 'anomaly') ||
      `Anomaly detected on ${data.assetId}: ${data.category} pattern affecting ${data.sensors.join(', ')}.`
  }

  // Generate maintenance insight
  generateMaintenanceInsight(data: {
    recommendations: {
      assetId: string
      action: string
      priority: string
      estimatedCost: number
      preventedDowntime: number
    }[]
    totalCost: number
    totalSavings: number
  }): string {
    const context: TemplateContext = {
      recommendationCount: data.recommendations.length,
      criticalCount: data.recommendations.filter(r => r.priority === 'critical').length,
      totalCostFormatted: formatCurrency(data.totalCost),
      totalSavingsFormatted: formatCurrency(data.totalSavings),
      roiMultiplier: (data.totalSavings / data.totalCost).toFixed(1),
      netBenefit: formatCurrency(data.totalSavings - data.totalCost)
    }

    return generateNarrative(this.templates, context, 'maintenance') ||
      `${data.recommendations.length} maintenance actions recommended. Estimated ROI: ${context.roiMultiplier}x.`
  }

  // Generate comparative analysis insight
  generateComparativeInsight(data: {
    assets: {
      assetId: string
      reliability: number
      availability: number
      mtbf: number
      mttr: number
      anomalyRate: number
    }[]
    bestPerformer: string
    worstPerformer: string
    fleetAverage?: {
      reliability: number
      availability: number
      mtbf: number
      mttr: number
    }
  }): string {
    if (data.assets.length < 2) {
      return 'Select at least 2 assets to generate comparative insights.'
    }

    const reliabilities = data.assets.map(a => a.reliability)
    const avgReliability = reliabilities.reduce((a, b) => a + b, 0) / reliabilities.length
    const spread = Math.max(...reliabilities) - Math.min(...reliabilities)

    const topPerformers = data.assets
      .filter(a => a.reliability >= 90)
      .map(a => a.assetId)

    const needsAttention = data.assets
      .filter(a => a.reliability < 85)
      .map(a => a.assetId)

    const bestAsset = data.assets.find(a => a.assetId === data.bestPerformer)
    const worstAsset = data.assets.find(a => a.assetId === data.worstPerformer)

    let narrative = `Comparative Analysis - ${data.assets.length} Assets:\n\n`

    // Performance Overview
    narrative += `Performance Overview:\n`
    narrative += `├── Average Reliability: ${formatPercent(avgReliability)}\n`
    narrative += `├── Performance Spread: ${spread.toFixed(1)}% (${spread < 10 ? 'tight clustering' : spread < 20 ? 'moderate variance' : 'high variance'})\n`
    narrative += `└── Best: ${data.bestPerformer} (${bestAsset ? formatPercent(bestAsset.reliability) : 'N/A'})\n\n`

    // Best Practices from Top Performers
    if (topPerformers.length > 0) {
      narrative += `Top Performers (${topPerformers.length}): ${topPerformers.join(', ')}\n`
      narrative += `✓ Consistent high reliability (>90%)\n`
      narrative += `✓ Lower MTTR indicates efficient maintenance\n\n`
    }

    // Areas for Improvement
    if (needsAttention.length > 0) {
      narrative += `Needs Attention (${needsAttention.length}): ${needsAttention.join(', ')}\n`
      if (worstAsset) {
        narrative += `• Lowest reliability: ${formatPercent(worstAsset.reliability)}\n`
        narrative += `• Anomaly rate: ${worstAsset.anomalyRate.toFixed(1)}%\n`
      }
      narrative += `\n`
    }

    // Recommendations
    narrative += `Recommendations:\n`
    if (spread > 15) {
      narrative += `• Large performance gap detected - analyze operational differences\n`
    }
    if (needsAttention.length > 0) {
      narrative += `• Focus maintenance resources on underperforming assets\n`
      narrative += `• Review operational parameters vs. top performers\n`
    }
    if (topPerformers.length > 0) {
      narrative += `• Document best practices from ${data.bestPerformer} for fleet-wide adoption\n`
    }

    return narrative
  }

  // Generate executive summary insight
  generateExecutiveInsight(data: {
    period: string
    overallReliability: number
    previousReliability: number
    unplannedDowntime: number
    previousDowntime: number
    predictionAccuracy: number
    costSavings: number
    criticalPrevented: number
    keyWins: string[]
    attentionRequired: string[]
  }): string {
    const context: TemplateContext = {
      ...data,
      reliabilityFormatted: formatPercent(data.overallReliability),
      reliabilityChange: formatPercent(data.overallReliability - data.previousReliability),
      reliabilityTrend: describeTrend(data.overallReliability, data.previousReliability),
      downtimeFormatted: formatDuration(data.unplannedDowntime),
      downtimeImprovement: formatPercent(
        ((data.previousDowntime - data.unplannedDowntime) / data.previousDowntime) * 100
      ),
      accuracyFormatted: formatPercent(data.predictionAccuracy),
      savingsFormatted: formatCurrency(data.costSavings),
      keyWinsFormatted: data.keyWins.map(w => `• ${w}`).join('\n'),
      attentionFormatted: data.attentionRequired.map(a => `• ${a}`).join('\n')
    }

    return generateNarrative(this.templates, context, 'executive') ||
      `${data.period} Summary: Reliability at ${formatPercent(data.overallReliability)}, ${data.criticalPrevented} critical failures prevented.`
  }
}

// Export singleton instance
export const narrativeGenerator = new NarrativeGenerator()
